import React, {useEffect, useMemo} from "react";

import {useFormik} from 'formik';
import {
    useGetExamsMutation,
    useGetComponentsMutation,
    useLazyGetAvailableServiceTypesQuery
} from 'store/post-results-services/postResultsServicesApi';
import {MultiSelect} from "components/extro/MultiSelect";
import {formatUTCDate, getPrsServiceTypeDescription, PrsStatus} from "core";
import {AutoSelect} from "components/extro/AutoSelect";

import {uniqBy} from 'lodash-es';

import {useFormFields} from '@britishcouncil/react-forms';
import {LoadingCard, PageLoader, TnCCheckbox} from '@britishcouncil/react-solas-ors3';
import {Button} from '@britishcouncil/react-solas';

import { isAtsType, isCiePrs } from "../../../core/const/PrsServiceType";

import * as S from './Step1.style';

export type CreatePRSStep1Fields = {
    sessionId: number;
    candidateId: number;
    serviceTypeId: number;
    registrationId: number;
    countryId: number;
    components: { value: number, label: string }[];
    isPrsConsent: boolean;
};

type Step1Props = {
    onNext: (data: CreatePRSStep1Fields) => void;
    initialValues: CreatePRSStep1Fields
}

enum ExistingPrs {
    NotExists = 1,
    CanBeCanceled = 2,
    CanNotBeCanceled = 3,
}

export const Step1 = (props: Step1Props) => {
    const formik = useFormik<CreatePRSStep1Fields>({
        initialValues: props.initialValues,
        onSubmit: (values) =>
            props.onNext(values)
    });

    const [getExamQuery, getExamMutation] = useGetExamsMutation();
    const [getComponentsQuery, getComponentsMutation] = useGetComponentsMutation();
    const [getAvailableServiceTypesQuery, getAvailableServiceTypesQueryData] = useLazyGetAvailableServiceTypesQuery();

    useEffect(() => {
        getAvailableServiceTypesQuery()
    }, [getAvailableServiceTypesQuery]);

    const notCancelledPrsExists = useMemo((): ExistingPrs | null => {
        if (formik.values.registrationId && getExamMutation.isSuccess && getExamMutation.data) {
            if (getExamMutation.data.length === 0) {
                return ExistingPrs.NotExists;
            }
            const existingPrses = getExamMutation.data
                .filter(r => r.registrationId === formik.values.registrationId);

            if (existingPrses.filter(r => r.prsStatus === PrsStatus.Unpaid).length > 0) {
                return ExistingPrs.CanBeCanceled;
            } else if (existingPrses
                .filter(r => r.prsStatus === PrsStatus.Ongoing
                    || r.prsStatus === PrsStatus.Submitted
                    || r.prsStatus === PrsStatus.Paid)
                .length > 0) {
                return ExistingPrs.CanNotBeCanceled;
            }

            return ExistingPrs.NotExists;
        }
        return null;
    }, [formik.values.registrationId, getExamMutation.data, getExamMutation.isSuccess]);

    useEffect(() => {
        if (formik.values.sessionId && formik.values.candidateId && formik.values.serviceTypeId) {
            getExamQuery({
                candidateId: formik.values.candidateId,
                sessionId: formik.values.sessionId,
                serviceTypeId: formik.values.serviceTypeId
            })
        }
    }, [formik.values.sessionId, formik.values.candidateId, formik.values.serviceTypeId, getExamQuery]);

    useEffect(() => {
        if (formik.values.registrationId && formik.values.serviceTypeId && 
          (notCancelledPrsExists === ExistingPrs.NotExists || isAtsType(formik.values.serviceTypeId) || !isCiePrs(formik.values.serviceTypeId))
        ) {
            getComponentsQuery({
                registrationId: formik.values.registrationId,
                serviceTypeId: formik.values.serviceTypeId
            })
        }
    }, [formik.values.registrationId, formik.values.serviceTypeId, notCancelledPrsExists, getComponentsQuery]);

    const fields = useFormFields(formik);
    
    const isFormIncomplete =
      !formik.values.sessionId ||
      !formik.values.candidateId ||
      !formik.values.serviceTypeId ||
      !formik.values.registrationId ||
      !formik.values.isPrsConsent ||
      formik.values.components.length === 0;

    const totalPrice = formik.values.components.reduce((sum, item) => {
        const correspondingComponent = (getComponentsMutation.data || []).find(
          (component) => component.componentId === item.value,
        );
        return sum + (correspondingComponent ? parseFloat(correspondingComponent.fee) : 0);
      }, 0);
    
    const currencyCode = getComponentsMutation.data?.[0]?.currencyCode;
    
    const { taxAmount, taxRate } = useMemo(() => {
      const exam = getExamMutation.data?.find((x) => x.registrationId === formik.values.registrationId);

      if (exam && exam.taxRate > 0) {
        return { taxAmount: totalPrice * exam.taxRate, taxRate: exam.taxRate * 100 };
      }
      return { taxAmount: 0, taxRate: 0 };
    }, [totalPrice, getExamMutation.data, formik.values.registrationId]);

    return getAvailableServiceTypesQueryData.isLoading ? (
      <LoadingCard />
    ) : !getAvailableServiceTypesQueryData.data ? (
      <S.StyledMessage variant="info" hideIcon={true}>
        No data
      </S.StyledMessage>
    ) : (
      <S.Form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <label>Session</label>
          <AutoSelect
            {...fields.sessionId}
            autoSelectEnabled={!formik.values.sessionId}
            isMulti={false}
            value={
              getAvailableServiceTypesQueryData.data
                .filter((x) => x.sessionId === formik.values.sessionId)
                .map((x) => ({ value: x.sessionId, label: x.sessionName }))[0]
            }
            options={uniqBy(getAvailableServiceTypesQueryData.data, 'sessionId').map((x) => ({
              value: x.sessionId,
              label: x.sessionName,
            }))}
            onChange={(e, actionMeta) => {
              formik.setValues({
                ...formik.values,
                sessionId: e?.value || 0,
                candidateId: 0,
                serviceTypeId: 0,
                registrationId: 0,
                components: [],
              });
            }}
          />
        </div>

        <div className="form-group" key={`candidate_for_${formik.values.sessionId}`}>
          <label>Candidate</label>
          <AutoSelect
            {...fields.candidateId}
            autoSelectEnabled={!formik.values.candidateId}
            isMulti={false}
            value={
              getAvailableServiceTypesQueryData.data
                .filter((x) => x.sessionId === formik.values.sessionId)
                .filter((x) => x.candidateId === formik.values.candidateId)
                .map((x) => ({ value: x.candidateId, label: x.candidateName }))[0]
            }
            isDisabled={!formik.values.sessionId}
            options={uniqBy(
              getAvailableServiceTypesQueryData.data.filter((x) => x.sessionId === formik.values.sessionId),
              'candidateId'
            ).map((x) => ({ value: x.candidateId, label: x.candidateName }))}
            onChange={(e, meta) => {
              formik.setValues({
                ...formik.values,
                candidateId: e?.value || 0,
                serviceTypeId: 0,
                registrationId: 0,
                components: [],
              });
            }}
          />
        </div>

        <div className="form-group" key={`serviceType_${formik.values.candidateId}`}>
          <label>Post results services type</label>
          <AutoSelect
            {...fields.serviceTypeId}
            autoSelectEnabled={!formik.values.serviceTypeId || getAvailableServiceTypesQueryData.isLoading}
            isMulti={false}
            value={
              getAvailableServiceTypesQueryData.data
                .filter((x) => x.sessionId === formik.values.sessionId)
                .filter((x) => x.candidateId === formik.values.candidateId)
                .filter((x) => x.serviceTypeId === formik.values.serviceTypeId)
                .map((x) => ({ value: x.serviceTypeId, label: x.serviceTypeName }))[0]
            }
            isDisabled={!formik.values.candidateId}
            options={uniqBy(
              getAvailableServiceTypesQueryData.data
                .filter((x) => x.sessionId === formik.values.sessionId)
                .filter((x) => x.candidateId === formik.values.candidateId),
              'serviceTypeId'
            ).map((x) => ({ value: x.serviceTypeId, label: x.serviceTypeName }))}
            onChange={(e) => {
              formik.setValues({
                ...formik.values,
                serviceTypeId: e?.value || 0,
                registrationId: 0,
                components: [],
              });
            }}
          />
        </div>
          {formik.values.serviceTypeId > 0 && (
          <S.StyledMessage variant="info" hideIcon={true}>
              <div dangerouslySetInnerHTML={{__html: getPrsServiceTypeDescription(formik.values.serviceTypeId)}}/>
          </S.StyledMessage>
          )}

          { formik.values.serviceTypeId > 0 &&
              <div style={{ position: 'relative', zIndex: 0 }}>
                <TnCCheckbox {...fields.isPrsConsent}>I give my consent to the Head of Examination Centre to request for (Post result services) and/or (Access to scripts) of the examination(s) listed above. By giving consent for Post result services, I understand that the final subject grade awarded to me may be lower than, higher than, or the same as the grade which was originally awarded for this subject.</TnCCheckbox>
              </div>
          }

        {getExamMutation.isLoading && <PageLoader />}

        {getExamMutation?.isSuccess && (
          <div className="form-group">
            <label>Exam</label>
            <AutoSelect
              {...fields.registrationId}
              autoSelectEnabled={!formik.values.registrationId}
              isMulti={false}
              value={
                (getExamMutation.data || [])
                  .filter((x) => x.registrationId === formik.values.registrationId)
                  .map((x) => ({ value: x.registrationId, label: x.exam }))[0]
              }
              isDisabled={!formik.values.serviceTypeId || getExamMutation.isLoading}
              options={(getExamMutation.data || []).map((x) => ({ value: x.registrationId, label: x.exam }))}
              onChange={(e) => {
                let values = { ...formik.values };
                if (e?.value) {
                  const countryId = (getExamMutation.data || []).find((x) => x.registrationId === e?.value)?.countryId;
                  values.countryId = countryId || 0;
                }
                formik.setValues({
                  ...values,
                  registrationId: e?.value || 0,
                  components: [],
                });
              }}
            />
          </div>
        )}

        {formik.values.registrationId !== 0 && (
          <S.StyledMessage variant="info" hideIcon={true}>
            <div>
              Registration deadline:{' '}
              <strong>
                {formatUTCDate(
                  getExamMutation.data?.find((x) => x.registrationId === formik.values.registrationId)?.deadline
                )}
              </strong>
            </div>
          </S.StyledMessage>
        )}

        {notCancelledPrsExists != null && notCancelledPrsExists !== ExistingPrs.NotExists && 
          !isAtsType(formik.values.serviceTypeId) && isCiePrs(formik.values.serviceTypeId) &&
        (
          <S.StyledMessage variant="warning" hideIcon={true}>
            {notCancelledPrsExists === ExistingPrs.CanBeCanceled &&
              `Post results service request has been created for the selected syllabus, please cancel the existing request to add a different request.`}
            {notCancelledPrsExists === ExistingPrs.CanNotBeCanceled &&
              `For chosen subject not cancelled request already exist`}
          </S.StyledMessage>
        )}

        {getComponentsMutation.isLoading && <PageLoader />}

        {(getComponentsMutation?.isSuccess || formik.values.registrationId !== 0) && (
          <>
            <div className="form-group">
              <label>Components</label>
              <MultiSelect
                {...fields.components}
                disableSearch={true}
                isLoading={getComponentsMutation.isLoading || getExamMutation.isLoading}
                hasSelectAll={false}
                disabled={notCancelledPrsExists !== ExistingPrs.NotExists &&  !isAtsType(formik.values.serviceTypeId) && isCiePrs(formik.values.serviceTypeId)}
                placeholder="Choose"
                labelledBy="Choose"
                value={formik.values.components}
                options={(getComponentsMutation.data || [])
                  .map((x) => ({
                    value: x.componentId,
                    label: x.component,
                    disabled: x.isDisabled,
                  }))}
                onChange={(selected: any) => {
                  formik.setFieldValue('components', selected);
                }}
              />
            </div>

            {(getComponentsMutation.data || []).some((x) => x.isDisabled) && (
              <S.StyledMessage variant="warning" hideIcon={true}>
                Please notice that some of components are not able to select because of awarding body restrictions or there are already created PRS requests for them.
              </S.StyledMessage>
            )}
          </>
        )}

        {formik.values.components && formik.values.components.length > 0 && getComponentsMutation.data && (
          <S.SummaryContainer>
            <div>
              {formik.values.components.map((item, index) => {
                const correspondingComponent = (getComponentsMutation.data || []).find(
                  (component) => component.componentId === item.value
                );

                return correspondingComponent ? (
                  <div key={index} className="item-row">
                    <div className="item-name">{correspondingComponent.component}:</div>
                    <div className="item-price">
                      {correspondingComponent.fee} {correspondingComponent.currencyCode}
                    </div>
                  </div>
                ) : null;
              })}
              {taxRate > 0 && (
                <div className="total-row">
                  <div>SST {taxRate}% Tax:</div>
                  <div className="total-price">
                    {taxAmount}&nbsp;{currencyCode}
                  </div>
                </div>
              )}
              <div className="total-row">
                <div>Total price:</div>
                <div className="total-price">
                  {totalPrice + taxAmount}&nbsp;{currencyCode}
                </div>
              </div>
            </div>
          </S.SummaryContainer>
        )}

        <S.ButtonContainer>
          <Button disabled={isFormIncomplete} size="xs" intent="primary" type="submit">
            Next
          </Button>
        </S.ButtonContainer>
      </S.Form>
    );

};

